/**
 * GameChart.tsx — MakeCode Arcade 게임 차트 (평점 / 인기)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import Button from '../../components/ui/Button';
import PlayArrowIcon from '../../components/icons/PlayArrowIcon';
import EmojiEventsIcon from '../../components/icons/EmojiEventsIcon';
import TrendingUpIcon from '../../components/icons/TrendingUpIcon';
import ArcadeGamePoster from './ArcadeGamePoster';

const DEFAULT_GAME_ICON = '/images/icons/Logo.svg';
const CHART_LIMIT = 20;
const PODIUM_COUNT = 3;

interface Game {
  id: string;
  title: string;
  description: string;
  gameUrl: string;
  simulatorCode: string;
  processUrl: string;
  companyName: string;
  creatorName: string;
  schoolName: string;
  schoolId: string;
  userId: string;
  createdAt: Date;
  rating: number;
  playCount: number;
  iconUrl?: string;
  introduction?: string;
}

type ChartKind = 'rating' | 'popular';
type MobileTab = 'rating' | 'popular';

const BRAND = 'var(--Color-fill-brand-color, #FF6F0F)';

const RANK_STYLES: Record<number, { badge: string; ring: string; label: string }> = {
  1: {
    badge: 'linear-gradient(135deg, #FFD54F 0%, #FFB300 100%)',
    ring: 'rgba(255, 179, 0, 0.35)',
    label: '#B8860B',
  },
  2: {
    badge: 'linear-gradient(135deg, #E8E8E8 0%, #BDBDBD 100%)',
    ring: 'rgba(158, 158, 158, 0.35)',
    label: '#616161',
  },
  3: {
    badge: 'linear-gradient(135deg, #FFCC80 0%, #FF8A65 100%)',
    ring: 'rgba(255, 138, 101, 0.35)',
    label: '#BF360C',
  },
};

function getMetricValue(game: Game, kind: ChartKind): number {
  return kind === 'rating' ? game.rating : game.playCount;
}

function formatMetric(value: number, kind: ChartKind): string {
  if (kind === 'rating') return value.toFixed(1);
  return value.toLocaleString();
}

/** 읽기 전용 별점 (0~5, 0.5 단위 반별 지원) */
const StarRatingDisplay: React.FC<{
  value: number;
  size?: number;
  showValue?: boolean;
}> = ({ value, size = 14, showValue = true }) => {
  const rating = Math.max(0, Math.min(5, value));

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = rating >= star;
        const half = !filled && rating >= star - 0.5;

        return (
          <span
            key={star}
            style={{
              position: 'relative',
              display: 'inline-block',
              width: size,
              height: size,
              fontSize: size,
              lineHeight: 1,
            }}
            aria-hidden
          >
            <span style={{ color: '#E0E0E0' }}>★</span>
            {(filled || half) && (
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  color: '#FFB800',
                  overflow: 'hidden',
                  width: filled ? '100%' : '50%',
                }}
              >
                ★
              </span>
            )}
          </span>
        );
      })}
      {showValue && (
        <span
          style={{
            marginLeft: 4,
            fontSize: Math.max(11, size - 1),
            fontWeight: 600,
            color: '#B8860B',
            fontFamily: 'var(--font-family-primary, Pretendard, sans-serif)',
          }}
        >
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

interface ChartSectionProps {
  kind: ChartKind;
  games: Game[];
  onGameClick: (id: string) => void;
  compact?: boolean;
}

const ChartSection: React.FC<ChartSectionProps> = ({ kind, games, onGameClick, compact }) => {
  const maxPlayCount = useMemo(
    () => Math.max(...games.map((g) => g.playCount), 1),
    [games]
  );

  const podium = games.slice(0, PODIUM_COUNT);
  const rest = games.slice(PODIUM_COUNT);

  const headerIcon =
    kind === 'rating' ? (
      <EmojiEventsIcon width={22} height={22} fill="#FFB300" />
    ) : (
      <TrendingUpIcon width={22} height={22} />
    );

  const headerTitle = kind === 'rating' ? '평점 TOP' : '인기 TOP';
  const headerSub = kind === 'rating' ? '별점이 높은 게임' : '플레이 수가 많은 게임';

  return (
    <section
      style={{
        flex: 1,
        minWidth: 0,
        background: '#1C1C28',
        borderRadius: 16,
        border: '1px solid #2E2E3C',
        boxShadow: '0 4px 24px rgba(41, 48, 68, 0.06)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: compact ? '16px 18px' : '20px 24px',
          background:
            kind === 'rating'
              ? 'linear-gradient(135deg, #2A2418 0%, #1C1C28 55%)'
              : 'linear-gradient(135deg, #2A2018 0%, #1C1C28 55%)',
          borderBottom: '1px solid #2E2E3C',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          {headerIcon}
          <h2
            style={{
              margin: 0,
              fontSize: compact ? 18 : 20,
              fontWeight: 700,
              color: '#F4F4F8',
              fontFamily: 'var(--font-family-primary, Pretendard, sans-serif)',
            }}
          >
            {headerTitle}
          </h2>
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: 'var(--Color-text-assistive, #989898)',
            fontFamily: 'var(--font-family-primary, Pretendard, sans-serif)',
          }}
        >
          {headerSub}
        </p>
      </div>

      <div style={{ padding: compact ? '16px' : '20px 24px', flex: 1 }}>
        {games.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 16px',
              color: '#989898',
              fontSize: 14,
            }}
          >
            아직 집계할 게임이 없습니다.
          </div>
        ) : (
          <>
            {podium.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: podium.length >= 3 ? '1fr 1.15fr 1fr' : `repeat(${podium.length}, 1fr)`,
                  gap: 12,
                  alignItems: 'end',
                  marginBottom: 20,
                }}
              >
                {[podium[1], podium[0], podium[2]].filter(Boolean).map((game) => {
                  if (!game) return null;
                  const rank = games.indexOf(game) + 1;
                  const style = RANK_STYLES[rank];
                  const isFirst = rank === 1;
                  const metric = getMetricValue(game, kind);

                  return (
                    <button
                      key={game.id}
                      type="button"
                      onClick={() => onGameClick(game.id)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        order: rank === 1 ? 2 : rank === 2 ? 1 : 3,
                      }}
                    >
                      <div
                        style={{
                          background: '#16161F',
                          borderRadius: 14,
                          padding: isFirst ? '16px 12px 14px' : '12px 10px',
                          border: `1px solid ${style.ring}`,
                          boxShadow: isFirst
                            ? '0 8px 24px rgba(255, 111, 15, 0.12)'
                            : '0 2px 12px rgba(0,0,0,0.06)',
                          transform: isFirst ? 'translateY(-6px)' : 'none',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          textAlign: 'center',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = isFirst ? 'translateY(-10px)' : 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = isFirst ? 'translateY(-6px)' : 'none';
                          e.currentTarget.style.boxShadow = isFirst
                            ? '0 8px 24px rgba(255, 111, 15, 0.12)'
                            : '0 2px 12px rgba(0,0,0,0.06)';
                        }}
                      >
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            margin: '0 auto 8px',
                            borderRadius: 8,
                            background: style.badge,
                            color: '#FFF',
                            fontWeight: 700,
                            fontSize: 13,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                          }}
                        >
                          {rank}
                        </div>
                        <div
                          style={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            borderRadius: 12,
                            overflow: 'hidden',
                            margin: '0 auto 8px',
                            border: `2px solid ${style.ring}`,
                          }}
                        >
                          <ArcadeGamePoster id={game.id} title={game.title} iconUrl={game.iconUrl} fontSize={isFirst ? 36 : 28} />
                        </div>
                        <div
                          style={{
                            fontSize: isFirst ? 14 : 13,
                            fontWeight: 600,
                            color: '#F4F4F8',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginBottom: 4,
                          }}
                        >
                          {game.title}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: style.label,
                          }}
                        >
                          {kind === 'rating' ? (
                            <StarRatingDisplay value={metric} size={isFirst ? 16 : 14} />
                          ) : (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <PlayArrowIcon width={14} height={14} />
                              {metric.toLocaleString()}회
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {rest.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rest.map((game) => {
                  const rank = games.indexOf(game) + 1;
                  const metric = getMetricValue(game, kind);
                  const playPct = Math.round((game.playCount / maxPlayCount) * 100);

                  return (
                    <button
                      key={game.id}
                      type="button"
                      onClick={() => onGameClick(game.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid transparent',
                        borderRadius: 12,
                        background: '#16161F',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontFamily: 'inherit',
                        transition: 'background 0.15s, border-color 0.15s, transform 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#22222E';
                        e.currentTarget.style.borderColor = 'var(--Color-line-normal, #F0F0F0)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#16161F';
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.transform = 'none';
                      }}
                    >
                      <span
                        style={{
                          width: 28,
                          flexShrink: 0,
                          fontSize: 14,
                          fontWeight: 600,
                          color: rank <= 10 ? '#989898' : '#BDBDBD',
                          textAlign: 'center',
                        }}
                      >
                        {rank}
                      </span>
                      <img
                        src={game.iconUrl || DEFAULT_GAME_ICON}
                        alt=""
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 10,
                          objectFit: 'cover',
                          flexShrink: 0,
                          background: '#F0F0F0',
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#F4F4F8',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginBottom: 2,
                          }}
                        >
                          {game.title}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: '#989898',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginBottom: kind === 'rating' ? 4 : 6,
                          }}
                        >
                          {game.creatorName}
                          {game.schoolName ? ` · ${game.schoolName}` : ''}
                        </div>
                        {kind === 'rating' ? (
                          <StarRatingDisplay value={game.rating} size={15} />
                        ) : (
                          <div
                            style={{
                              height: 4,
                              borderRadius: 2,
                              background: '#EEE',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: `${playPct}%`,
                                height: '100%',
                                borderRadius: 2,
                                background: `linear-gradient(90deg, ${BRAND}99, ${BRAND})`,
                                transition: 'width 0.4s ease',
                              }}
                            />
                          </div>
                        )}
                      </div>
                      {kind === 'popular' && (
                        <div
                          style={{
                            flexShrink: 0,
                            fontSize: 13,
                            fontWeight: 600,
                            color: BRAND,
                            minWidth: 48,
                            textAlign: 'right',
                          }}
                        >
                          {formatMetric(metric, kind)}
                          <span style={{ fontSize: 11, fontWeight: 400, color: '#989898' }}> 회</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

const GameChart: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useLayoutContext();

  const [loading, setLoading] = useState(true);
  const [ratedGames, setRatedGames] = useState<Game[]>([]);
  const [popularGames, setPopularGames] = useState<Game[]>([]);
  const [mobileTab, setMobileTab] = useState<MobileTab>('rating');

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const ratedQuery = query(collection(db, 'games'), orderBy('rating', 'desc'), limit(CHART_LIMIT));
        const popularQuery = query(collection(db, 'games'), orderBy('playCount', 'desc'), limit(CHART_LIMIT));

        const [ratedSnapshot, popularSnapshot] = await Promise.all([
          getDocs(ratedQuery),
          getDocs(popularQuery),
        ]);

        const mapDoc = (docSnap: (typeof ratedSnapshot.docs)[0]) => ({
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: docSnap.data().createdAt?.toDate?.() ?? new Date(),
        }) as Game;

        setRatedGames(ratedSnapshot.docs.map(mapDoc));
        setPopularGames(popularSnapshot.docs.map(mapDoc));
      } catch (error) {
        console.error('Error fetching games:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, []);

  const handleGameClick = (gameId: string) => {
    navigate(`/makecodearcade/game/${gameId}`);
  };

  if (loading) {
    return (
      <div
        style={{
          ...getMakecodeArcadePageRootStyle(isMobile),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 320,
          gap: 16,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            border: '3px solid #F0F0F0',
            borderTopColor: BRAND,
            borderRadius: '50%',
            animation: 'chartSpin 0.8s linear infinite',
          }}
        />
        <span style={{ fontSize: 14, color: '#989898' }}>차트 불러오는 중…</span>
        <style>{`@keyframes chartSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={getMakecodeArcadePageRootStyle(isMobile)}>
      <header style={{ marginBottom: isMobile ? 20 : 28 }}>
        <h1
          style={{
            margin: '0 0 8px',
            fontSize: isMobile ? 26 : 32,
            fontWeight: 700,
            color: '#F4F4F8',
            fontFamily: 'var(--font-family-primary, Pretendard, sans-serif)',
            letterSpacing: '-0.02em',
          }}
        >
          게임 차트
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: 15,
            color: 'var(--Color-text-assistive, #989898)',
            lineHeight: 1.5,
            fontFamily: 'var(--font-family-primary, Pretendard, sans-serif)',
          }}
        >
          평점과 플레이 수 기준 상위 게임을 확인해 보세요.
        </p>
      </header>

      {isMobile && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 20,
            padding: 4,
            background: '#1C1C28',
            borderRadius: 12,
          }}
        >
          {(
            [
              { id: 'rating' as const, label: '평점', icon: <EmojiEventsIcon width={18} height={18} fill="#FFB300" /> },
              { id: 'popular' as const, label: '인기', icon: <TrendingUpIcon width={18} height={18} /> },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setMobileTab(tab.id)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '10px 12px',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                background: mobileTab === tab.id ? '#2A2A38' : 'transparent',
                color: mobileTab === tab.id ? '#F4F4F8' : '#989898',
                boxShadow: mobileTab === tab.id ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                transition: 'all 0.2s',
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 0 : 24,
          alignItems: 'stretch',
        }}
      >
        {(!isMobile || mobileTab === 'rating') && (
          <ChartSection kind="rating" games={ratedGames} onGameClick={handleGameClick} compact={isMobile} />
        )}
        {(!isMobile || mobileTab === 'popular') && (
          <ChartSection kind="popular" games={popularGames} onGameClick={handleGameClick} compact={isMobile} />
        )}
      </div>

      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <Button variant="outline" onClick={() => navigate('/makecodearcade/store')}>
          게임 스토어로 이동
        </Button>
      </div>
    </div>
  );
};

export default GameChart;
